import { motion } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Home, Truck, DollarSign, Package, AlertTriangle } from 'lucide-react'

interface NavItem {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  badge?: number
}

const navItems: NavItem[] = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/active-orders', label: 'Orders', icon: Truck },
  { path: '/earnings', label: 'Earnings', icon: DollarSign },
  { path: '/orders', label: 'History', icon: Package },
  { path: '/emergency', label: 'SOS', icon: AlertTriangle },
]

export default function MobileBottomNav() {
  const location = useLocation()
  const navigate = useNavigate()

  const handleNavigation = (path: string) => {
    // Add haptic feedback if available
    if ('vibrate' in navigator) {
      navigator.vibrate(10)
    }
    navigate(path)
  }

  return (
    <motion.nav
      className="fixed bottom-0 left-0 right-0 bg-background border-t border-border safe-area-bottom md:hidden z-50"
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className="flex items-center justify-around px-2 py-2">
        {navItems.map((item) => {
          const Icon = item.icon
          const isActive = location.pathname === item.path || 
            (item.path !== '/' && location.pathname.startsWith(item.path))

          return (
            <motion.button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className={cn(
                'relative flex flex-col items-center justify-center px-3 py-2 rounded-lg min-h-[60px] min-w-[60px] touch-feedback',
                isActive ? 'text-primary' : 'text-muted-foreground'
              )}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            >
              {/* Active indicator */}
              {isActive && (
                <motion.div
                  className="absolute inset-0 bg-primary/10 rounded-lg"
                  layoutId="activeTab"
                  initial={false}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 30
                  }}
                />
              )}

              <div className="relative z-10 flex flex-col items-center">
                <motion.div
                  animate={isActive ? { scale: 1.1 } : { scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  className="relative"
                >
                  <Icon className="w-6 h-6" />
                  
                  {/* Badge */}
                  {item.badge && item.badge > 0 && (
                    <motion.div
                      className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </motion.div>
                  )}
                </motion.div>

                <span className={cn(
                  'text-xs mt-1 font-medium transition-colors',
                  isActive ? 'text-primary' : 'text-muted-foreground'
                )}>
                  {item.label}
                </span>
              </div>
            </motion.button>
          )
        })}
      </div>
    </motion.nav>
  )
}